import { useState, useRef, useEffect } from "react";

interface EditableTitleProps {
  title: string;
  onTitleChange?: (newTitle: string) => void;
  onCommandExecute?: (command: string) => void;
  onFilter?: (filterText: string) => void;
  className?: string;
  id?: string;
}

export function EditableTitle({ title, onTitleChange, onCommandExecute, onFilter, className = "feed-card-title", id }: EditableTitleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditedTitle(title);
  }, [title]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editedTitle.trim() && editedTitle !== title && onTitleChange) {
      onTitleChange(editedTitle.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedTitle(title);
    setIsEditing(false);
    // Clear filter on cancel
    if (onFilter) {
      onFilter('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditedTitle(newValue);
    // Apply filter during typing
    if (onFilter) {
      onFilter(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Execute command if callback provided
      if (onCommandExecute && editedTitle.trim()) {
        onCommandExecute(editedTitle.trim());
        setIsEditing(false);
        // Clear filter after command execution
        if (onFilter) {
          onFilter('');
        }
      } else {
        handleSave();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        className={`${className} ${className}-edit`}
        value={editedTitle}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        id={id}
      />
    );
  }

  return (
    <h3 
      id={id} 
      className={`${className} ${className}-editable`}
      onClick={handleClick}
      title="Klicken zum Bearbeiten"
    >
      {title}
    </h3>
  );
}
